'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Cirkul Have Microplastics? Bottle, Sip Cartridges & Flavor System Audit (2026)"
      author="MicroPlastics Research Desk"
      date="July 5, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Does Cirkul have microplastics. Tritan bottle, stainless line, and Sip flavor cartridge audited"
    >
      <BlogLastUpdated iso="2026-07-05" formatted="July 5, 2026" />

      <BlogTLDR>
        <strong>It depends on which Cirkul you buy, and how you treat it.</strong>{' '}
        The standard Cirkul bottle is <strong>Tritan copolyester</strong>: a
        BPA-free plastic, but still a plastic, with a genuinely contested
        research record on chemical leaching. On top of that, Cirkul&apos;s
        signature feature works against it: <strong>every sip passes through a
        single-use plastic flavor cartridge and its valve</strong>, and the
        flavor concentrate is mildly acidic (citric and malic acid), which
        nudges migration upward versus plain water. In Cirkul&apos;s favor: the
        system is designed for <strong>cold water only</strong>, and heat is by
        far the biggest driver of plastic shedding. No lab has published a
        Cirkul-specific microplastics measurement, none exists, so this is a
        materials audit, not a test result. The honest verdict:{' '}
        <strong>Cirkul&apos;s stainless-steel bottle with moderate cartridge use
        is a reasonable setup</strong>; the plastic bottle left in a hot car and
        run through the dishwasher is the pattern to avoid.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The standard Cirkul bottle is <strong>Tritan copolyester</strong> (Eastman&apos;s BPA-free plastic). BPA-free is a real claim about one chemical, it is not the same as &ldquo;leach-free&rdquo; or &ldquo;microplastic-free.&rdquo;</>,
          <>The <strong>Sip cartridge is the unusual exposure path</strong>: water flows through a plastic cartridge and valve on every single sip, and you drink from the cartridge&apos;s own spout. No other mainstream bottle routes 100% of your water through disposable plastic.</>,
          <>Cirkul flavors are mildly acidic (citric/malic acid), and acidic liquid in plastic contact modestly increases chemical migration versus plain cold water, a qualitative nudge, not a hazard headline.</>,
          <><strong>Cold-only use is genuinely in Cirkul&apos;s favor.</strong> Heat is the dominant variable in plastic shedding. Li et al. (2020) found polypropylene bottles released millions of microplastics per litre with hot water versus far less when cold.</>,
          <><strong>Cirkul sells a stainless-steel bottle line</strong>, that is the version to buy. Steel body + cold water + hand-washed lid keeps the only plastic contact at the cartridge itself.</>,
          <>No Cirkul-specific microplastic study has been published. Everything here is audited from the materials and the general migration literature, we say so explicitly rather than inventing numbers.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_cirkul" />

      <KeyStats
        heading="Cirkul & microplastics, the materials facts"
        stats={[
          { value: 'Tritan™', label: 'standard Cirkul bottle material', context: 'Eastman’s BPA-free copolyester, a plastic, with a contested leaching-research record', source: 'Eastman Tritan material documentation', sourceUrl: 'https://www.eastman.com/en/products/product-detail/71050612/tritan-copolyester-tx1001' },
          { value: '0 studies', label: 'published Cirkul-specific microplastic tests', context: 'no lab has measured Cirkul bottles or cartridges, this audit works from materials', source: 'Literature search, June 2026', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/' },
          { value: 'every sip', label: 'passes through the plastic Sip cartridge', context: 'water flows through the cartridge body and valve, and you drink from its spout', source: 'Cirkul product design', sourceUrl: 'https://drinkcirkul.com/' },
          { value: '1–16 M/L', label: 'microplastics from PP bottles at 70–95°C', context: 'the heat effect Cirkul’s cold-only design avoids, why temperature dominates', source: 'Li et al. 2020, Nature Food', sourceUrl: 'https://www.nature.com/articles/s43016-020-00171-y' },
          { value: '18/8 steel', label: 'Cirkul’s stainless bottle line', context: 'zero microplastic shedding from the body, the pick if you want the flavor system', source: 'Cirkul stainless product line', sourceUrl: 'https://drinkcirkul.com/' },
          { value: 'single-use', label: 'every Sip cartridge is disposable plastic', context: 'a heavy Cirkul habit adds dozens of plastic cartridges per year to the waste stream', source: 'Cirkul cartridge design', sourceUrl: 'https://drinkcirkul.com/' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        First, the honest caveat: nobody has tested Cirkul in a lab
      </h2>
      <p>
        Before any verdict: <strong>there is no published, peer-reviewed
        microplastics measurement of a Cirkul bottle or Sip cartridge</strong>.
        Not one. Cirkul went from a startup to Walmart&apos;s top-selling water
        bottle brand fast enough that the research literature simply hasn&apos;t
        caught up. So anyone giving you a particles-per-litre number for Cirkul
        is making it up.
      </p>
      <p>
        What we <em>can</em> do (and what this article does) is audit the
        system from its materials, the same way we audited{' '}
        <Link href="/blog/stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics" className="text-[#1E6B47] underline hover:text-[#155235]">Stanley, Yeti, Hydro Flask and Owala</Link>.
        The polymers Cirkul uses are well studied. The conditions that make
        plastics shed (heat, acid, mechanical wear, age) are well studied. Map
        one onto the other and you get a defensible picture, clearly labelled
        as inference rather than measurement.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The standard Cirkul bottle is Tritan. BPA-free, but still a plastic
      </h2>
      <p>
        Cirkul&apos;s standard bottles are made of <strong>Tritan</strong>,
        Eastman Chemical&apos;s copolyester (the TX1001-family resins). It&apos;s
        the same clear, tough, BPA-free plastic used in the Owala FreeSip spout,
        Nalgene bottles, and most premium clear drinkware. &ldquo;BPA-free&rdquo;
        is true and worth something. Tritan is genuinely made without
        bisphenol&nbsp;A. But it answers a narrower question than most buyers
        think they&apos;re asking.
      </p>
      <p>
        <strong>BPA-free does not mean leach-free.</strong> Two things can still
        come out of any plastic bottle: polymer microparticles shed by abrasion,
        heat and age, and non-polymer chemicals (residual monomers, additives)
        that migrate into the water. On the second point, Tritan has a genuinely
        contested research history worth laying out fairly:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>The critical side:</strong> Yang et al. (2011) and Bittner et al. (2014) used cell-based fluorescence assays and reported that many BPA-free products (including some Tritan items, particularly after UV or heat stress) released chemicals with detectable <em>estrogenic activity</em>.</li>
        <li><strong>The industry side:</strong> Eastman-commissioned testing (Osimitz et al. 2012) found no estrogenic or androgenic activity from Tritan&apos;s three monomers, and Eastman sued the lab behind the critical studies, a jury sided with Eastman in 2013, finding the &ldquo;EA-free&rdquo; marketing dispute in its favor.</li>
        <li><strong>Where it stands:</strong> the assay methods, stress conditions, and commercial interests on <em>both</em> sides are contested. As microplastics coverage surged through 2021–2023, the Tritan debate resurfaced without ever being cleanly resolved. The fair summary: Tritan is very likely among the better-behaved clear plastics, and it is still a plastic whose &ldquo;nothing leaches&rdquo; claim rests partly on industry-funded work.</li>
      </ul>
      <p>
        For microparticle shedding specifically, copolyesters like Tritan behave
        like their PET cousins: minimal release cold, meaningfully more with
        heat, abrasion, and age. Our{' '}
        <Link href="/blog/refillable-water-bottle-materials-compared" className="text-[#1E6B47] underline hover:text-[#155235]">bottle materials comparison</Link>{' '}
        covers where Tritan sits in the full materials ranking.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Sip cartridge, the part no other bottle has
      </h2>
      <p>
        The cartridge is what makes Cirkul, Cirkul, and it&apos;s the most
        interesting part of this audit. A Sip is a small plastic cartridge of
        flavor concentrate that twists into the lid. Water leaves the bottle,{' '}
        <strong>flows through the cartridge body and its plastic-and-silicone
        valve, picks up concentrate at whatever the dial is set to, and exits
        through the cartridge&apos;s own spout into your mouth</strong>. With a
        steel Stanley or Yeti, plastic contact is a straw or a lid rim. With
        Cirkul, 100% of the water you drink transits a disposable plastic
        flow path on every sip, even with the dial at zero.
      </p>
      <p>
        Three things follow from that design:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>The cartridge is the floor on plastic contact.</strong> You can upgrade the bottle to steel, but you cannot take the plastic out of the sip path, it <em>is</em> the sip path. That makes Cirkul&apos;s minimum plastic contact higher than an all-stainless bottle can ever be.</li>
        <li><strong>Fresh plastic, constantly.</strong> Cartridges are single-use and replaced every few days to weeks depending on use. Fresh plastic surfaces tend to release more residual small molecules early in their life than well-washed, aged surfaces, you restart that clock with every new Sip.</li>
        <li><strong>The waste angle is real.</strong> A daily Cirkul habit can run through dozens of cartridges a year, small, mixed-material plastic units that are generally not curbside-recyclable. Whatever the personal-exposure math, the microplastic <em>source</em> math of disposable cartridges vs a plain refillable is not close.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Acidic flavors meet plastic, a mild but real nudge
      </h2>
      <p>
        Cirkul&apos;s flavor concentrates lean on <strong>citric acid and malic
        acid</strong>, the standard sour-fruit acids in most zero-sugar drink
        mixes. Food-contact migration testing has long used acidic simulants
        precisely because <strong>mildly acidic liquids extract more from
        plastics than plain water does</strong>. It&apos;s the reason regulators
        test orange-juice conditions separately from water conditions.
      </p>
      <p>
        Keep this in proportion: at fridge-to-room temperature, the effect is a
        modest multiplier on a small baseline, not a hazard in itself. But it
        stacks in one direction. Flavored acidic water sitting against a Tritan
        bottle wall all day, plus an acidic concentrate stream through a plastic
        valve, is mildly more extractive than the plain cold tap water an
        unflavored steel bottle holds. If you run the dial high all day, every
        day, you&apos;re at the top of Cirkul&apos;s exposure envelope; dial low
        or at zero, you&apos;re near the bottom.
      </p>

      <BlogAppCTA
        headline="Wondering about the rest of what you drink?"
        sub="Bottles are one exposure path of many. Scan your drinks, snacks and kitchen gear with the MicroPlastics app to see a 0–100 risk score per product and where your real exposure comes from."
        buttonLabel="Scan my daily drinks"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What&apos;s genuinely in Cirkul&apos;s favor: it&apos;s a cold-water system
      </h2>
      <p>
        Fairness cuts both ways, and Cirkul has one big structural advantage:{' '}
        <strong>nobody puts hot liquid in a Cirkul</strong>. The system is
        designed and marketed for cold water only, hot drinks would dump the
        flavor system entirely. And heat is the single biggest lever in the
        plastic-migration literature. Li et al. (2020) measured polypropylene
        baby bottles releasing on the order of <strong>1–16 million
        microplastic particles per litre</strong> during hot (70–95°C) formula
        preparation, versus dramatically lower release at room temperature.
        Cold use doesn&apos;t make a plastic bottle shed <em>nothing</em>, but
        it parks Cirkul at the low end of its material&apos;s range for the
        actual drinking part.
      </p>
      <p>
        The heat risk instead comes from two off-label moments:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>The hot car.</strong> A Tritan bottle of flavored water left in a summer car interior (routinely 50–70°C) is exactly the warm-acidic-liquid-in-plastic scenario the migration literature flags. We covered the mechanism in{' '}
          <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-[#1E6B47] underline hover:text-[#155235]">what happens to a water bottle left in a hot car</Link>. If it cooked all afternoon, dump it, rinse, refill.</li>
        <li><strong>The dishwasher.</strong> Tritan is marketed as top-rack dishwasher-safe, and mechanically it holds up. But repeated 60–70°C wash cycles with alkaline detergent are a recurring heat-and-chemistry stress that ages the polymer surface, the condition under which the estrogenic-activity findings above were strongest, and under which shedding rises. Hand-washing the bottle and lid in warm (not scalding) water is the low-exposure habit.</li>
      </ul>
      <p>
        Put simply: <strong>a cold-only, hand-washed Cirkul is a much smaller
        question mark than the same bottle run hot.</strong> The bad pattern is
        specific, plastic bottle, hot car, dishwasher, repeat.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cirkul vs Owala vs Stanley vs plain steel, head to head
      </h2>
      <p>
        Here&apos;s how the two Cirkul configurations sit against the bottles
        from our{' '}
        <Link href="/blog/stanley-vs-yeti-vs-hydroflask-vs-owala-microplastics" className="text-[#1E6B47] underline hover:text-[#155235]">four-brand steel audit</Link>{' '}, ranked on the things that actually differ: plastic contact per sip,
        how the setup handles heat, and whether a flavor system adds a plastic
        flow path.
      </p>

      <BlogComparisonTable
        caption="Cirkul (plastic and steel) vs Owala, Stanley, and plain steel, microplastic-relevant comparison (2026)"
        columns={[
          { key: 'bottle', label: 'Bottle' },
          { key: 'contact', label: 'Plastic contact per sip' },
          { key: 'heat', label: 'Heat resilience' },
          { key: 'flavor', label: 'Flavor-system exposure' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { bottle: 'Cirkul (standard Tritan bottle)', contact: 'Highest (Tritan body + cartridge + valve in every sip', heat: 'Weakest) Tritan body suffers in hot cars and dishwashers', flavor: 'Yes: 100% of water transits the Sip cartridge', verdict: 'Fine used cold; worst of the five under heat' },
          { bottle: 'Cirkul stainless steel', contact: 'Moderate, steel body; cartridge + valve remain in the sip path', heat: 'Good body; keep the lid and cartridge out of heat', flavor: 'Yes: same cartridge flow path', verdict: 'The Cirkul to buy if you want the Sip system' },
          { bottle: 'Owala FreeSip', contact: 'Moderate (steel body; Tritan spout and straw', heat: 'Good) steel body, hand-wash the lid', flavor: 'None', verdict: 'Steel body, plastic-forward lid' },
          { bottle: 'Stanley Quencher H2.0', contact: 'Moderate (steel body; PP straw (swappable for stainless)', heat: 'Good) steel body, PP straw ages with heat', flavor: 'None', verdict: 'Swap the straw to stainless and it ranks higher' },
          { bottle: 'Plain 18/8 steel bottle + tap water', contact: 'Minimal (silicone gasket only with a steel lid', heat: 'Excellent) nothing meaningful to degrade', flavor: 'None', verdict: 'The lowest-exposure baseline everything is measured against' },
        ]}
      />
      <p className="text-sm text-gray-500">
        Rankings are a materials-based inference, not lab measurements of these
        SKUs, see the caveat at the top. For the full materials picture, see{' '}
        <Link href="/blog/refillable-water-bottle-materials-compared" className="text-[#1E6B47] underline hover:text-[#155235]">refillable bottle materials compared</Link>{' '}
        and our{' '}
        <Link href="/microplastics-in-water" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in water hub</Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The kids and teens question. Cirkul&apos;s heaviest users
      </h2>
      <p>
        Cirkul&apos;s growth story runs through school hallways: it&apos;s a
        Walmart best-seller with an intensely Gen&nbsp;Z and teen user base, and
        Cirkul sells kid-sized bottles. That matters for two reasons. First,{' '}
        <strong>exposure per kilogram of body weight</strong>: the same
        particle-and-additive dose is a bigger relative dose in a 30&nbsp;kg
        child than an 80&nbsp;kg adult, and developing endocrine systems are the
        population the estrogenic-activity debate is actually about. Second,{' '}
        <strong>habits</strong>: a teen&apos;s bottle lives in backpacks, hot
        parked cars, and the dishwasher, the exact heat pattern that pushes a
        Tritan bottle toward the top of its shedding range.
      </p>
      <p>
        None of this makes Cirkul dangerous for a kid, cold flavored water in a
        Tritan bottle is a modest exposure by any reading of the literature. But
        if the flavor system is what gets your kid off soda, the sensible
        version is the <strong>steel Cirkul bottle, dial moderate, hand-washed,
        never left cooking in a car</strong>. For the broader roundup, see{' '}
        <Link href="/blog/best-kids-water-bottles-without-microplastics" className="text-[#1E6B47] underline hover:text-[#155235]">the best kids water bottles without microplastics</Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The practical verdict
      </h2>
      <p>
        Should you throw out a Cirkul? No. Should you pretend it&apos;s a steel
        bottle with a fun lid? Also no. The honest ranking of ways to run a
        Cirkul, best to worst:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Cirkul stainless bottle, cold water, moderate dial, hand-washed.</strong> Plastic contact is confined to the cartridge flow path. This is a reasonable setup that we&apos;d place near Owala-with-FreeSip territory.</li>
        <li><strong>Tritan Cirkul, cold-only, hand-washed, never heat-soaked.</strong> Acceptable, the cold-only design does real work here. You&apos;re carrying the Tritan question mark, but at the low end of its range.</li>
        <li><strong>Tritan Cirkul + hot car afternoons + dishwasher cycles + dial-9 acidic flavor all day.</strong> This is the pattern to break. Every variable that raises plastic migration (heat, acid, surface aging, constant fresh plastic) is switched on at once.</li>
      </ol>
      <p>
        And if the flavor was never really the point, a plain{' '}
        <Link href="/blog/best-stainless-steel-water-bottles" className="text-forest underline">
          18/8 steel bottle
        </Link>{' '}
        with filtered tap remains the boring, unbeatable baseline.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The bottle&apos;s body material from the product scan. Tritan copolyester vs 18/8 stainless on the Cirkul line, and the equivalent check for Owala, Stanley and off-brand dupes.</>,
          <>Flavor-system and lid construction: whether your sip path runs through plastic, and what the cleaner same-brand configuration is.</>,
          <>A 0–100 microplastic risk score per bottle setup, so &ldquo;steel Cirkul, cold, hand-washed&rdquo; and &ldquo;Tritan bottle in the dishwasher&rdquo; stop being the same product in your head.</>,
          <>A running exposure log across everything you scan (drinks, cartridges, kitchenware) so you can see whether swapping bottles actually moved your number.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan your Cirkul before you decide"
        sub="The MicroPlastics app reads the product and shows the body material, the plastic in the sip path, a 0–100 risk score, and the cleaner swap (steel Cirkul, Owala, or plain stainless) in about ten seconds."
        buttonLabel="Scan my bottle"
      />

      <BlogFAQ
        items={[
          {
            question: 'Is Cirkul BPA-free?',
            answer:
              'Yes. Cirkul’s standard bottles are made of Tritan, Eastman’s copolyester that is manufactured without bisphenol A, and the brand markets the bottles and Sip cartridges as BPA-free. The important nuance is that BPA-free is a claim about one chemical, not a guarantee against all leaching: Tritan is still a plastic, and independent researchers (Yang 2011, Bittner 2014) reported estrogenic activity from some stressed Tritan products, findings Eastman disputes with its own commissioned studies. BPA-free is real, but it is not the same as leach-free or microplastic-free.',
          },
          {
            question: 'Are Cirkul cartridges toxic?',
            answer:
              'There is no evidence they are toxic, and no published lab test of Cirkul cartridges exists either way. The cartridge is a food-contact plastic unit whose concentrate and water path meet the same FDA food-contact rules as other drinkware. The reasonable concern is lower-grade: every sip passes through a plastic cartridge and valve carrying mildly acidic flavor concentrate, which means more plastic contact per sip than any conventional bottle, plus a steady stream of single-use plastic waste. That is an exposure and waste consideration, not a toxicity finding.',
          },
          {
            question: 'Is the Cirkul stainless steel bottle safer?',
            answer:
              'For microplastics, yes, it is the clear pick within the Cirkul lineup. The stainless version replaces the Tritan copolyester body with 18/8 stainless steel, which sheds essentially zero microplastics and shrugs off heat, removing the biggest variable (a plastic body in hot cars and dishwashers). The Sip cartridge flow path is unchanged, so it does not make Cirkul plastic-free, but it confines plastic contact to the cartridge itself.',
          },
          {
            question: 'Can you put a Cirkul bottle in the dishwasher?',
            answer:
              'Cirkul markets the bottles as top-rack dishwasher-safe, so mechanically yes. From a microplastics standpoint it is the habit we would skip for the Tritan bottle: repeated hot, alkaline wash cycles age the polymer surface, and heat-stressed Tritan is exactly the condition under which the contested estrogenic-activity findings were strongest and particle shedding rises. Hand-wash the plastic bottle and lid in warm water; the stainless body tolerates the dishwasher far better.',
          },
          {
            question: 'Is Cirkul good for kids?',
            answer:
              'Used sensibly, it is an acceptable option, cold flavored water in a BPA-free bottle is a modest exposure, and if it replaces soda that is a real win. Kids are also the group with the least margin: the same dose is larger per kilogram of body weight, and kids’ bottles see the most hot cars and dishwasher cycles. The low-exposure version for a child is the stainless Cirkul body, a moderate dial setting, hand-washing, and never drinking from a bottle that has cooked in a car. See our kids water bottle guide for plastic-free alternatives.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Yang CZ, Yaniger SI, Jordan VC, Klein DJ, Bittner GD.',
            year: 2011,
            title: 'Most plastic products release estrogenic chemicals: a potential health problem that can be solved',
            publisher: 'Environmental Health Perspectives',
            url: 'https://ehp.niehs.nih.gov/doi/10.1289/ehp.1003220',
          },
          {
            authors: 'Bittner GD, Yang CZ, Stoner MA.',
            year: 2014,
            title: 'Estrogenic chemicals often leach from BPA-free plastic products that are replacements for BPA-containing polycarbonate products',
            publisher: 'Environmental Health',
            url: 'https://ehjournal.biomedcentral.com/articles/10.1186/1476-069X-13-41',
          },
          {
            authors: 'Osimitz TG, Eldridge ML, Sloter E, et al.',
            year: 2012,
            title: 'Lack of androgenicity and estrogenicity of the three monomers used in Eastman’s Tritan copolyesters (Eastman-commissioned)',
            publisher: 'Food and Chemical Toxicology',
            url: 'https://www.sciencedirect.com/science/article/pii/S0278691512001404',
          },
          {
            authors: 'Li D, Shi Y, Yang L, et al.',
            year: 2020,
            title: 'Microplastic release from the degradation of polypropylene feeding bottles during infant formula preparation',
            publisher: 'Nature Food',
            url: 'https://www.nature.com/articles/s43016-020-00171-y',
          },
          {
            authors: 'Zangmeister CD, Radney JG, Benkstein KD, Kalanyan B.',
            year: 2022,
            title: 'Common single-use consumer plastics release trillions of nanoparticles per litre into water',
            publisher: 'Environmental Science & Technology Letters',
            url: 'https://pubs.acs.org/doi/10.1021/acs.estlett.2c00232',
          },
          {
            authors: 'Eastman Chemical Company',
            year: 2024,
            title: 'Tritan copolyester TX1001: material safety and third-party testing documentation',
            publisher: 'Eastman',
            url: 'https://www.eastman.com/en/products/product-detail/71050612/tritan-copolyester-tx1001',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
